import React, { createContext, useState, useContext, useEffect } from 'react';
import storageServiceFactory from './StorageServiceFactory';
import LocalStorageService from './StorageService';

// Create context for storage service
const StorageContext = createContext(null);

/**
 * Provider component that initializes the storage service
 * and provides it through context to all children
 */
export const StorageProvider = ({ children }) => {
  const [storageService, setStorageService] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authTokenGetter, setAuthTokenGetter] = useState(null);

  // Function to set auth token getter and trigger re-evaluation
  const updateAuthTokenGetter = (getAuthToken) => {
    console.log('StorageContext: Auth token getter updated');
    setAuthTokenGetter(() => getAuthToken); // Use function to avoid stale closures
    storageServiceFactory.setAuthTokenGetter(getAuthToken);
  };

  useEffect(() => {
    // Initialize the storage service when component mounts
    const initializeStorage = async () => {
      try {
        console.log('StorageContext: Initializing storage service factory');
        
        // Initialize storage service factory
        await storageServiceFactory.initialize();
        
        // Get the appropriate service (starts with localStorage)
        const service = await storageServiceFactory.getService();
        console.log(`StorageContext: Using ${storageServiceFactory.isUsingSQLite() ? 'SQLite' : 'localStorage'} storage service`);
        
        setStorageService(service);
        setIsInitialized(true);
      } catch (error) {
        console.error('StorageContext: Error initializing storage service', error);
        // Fallback to local storage in case of error
        console.log('StorageContext: Falling back to local storage');
        setStorageService(LocalStorageService);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStorage();
  }, []);

  // Re-evaluate storage service when auth becomes available
  useEffect(() => {
    if (!authTokenGetter || !isInitialized) {
      return;
    }

    const recheckStorageService = async () => {
      try {
        console.log('StorageContext: Re-evaluating storage service with authentication');
        
        // Get the service again - this time it will check SQLite availability
        const service = await storageServiceFactory.getService();
        const isUsingSQLite = storageServiceFactory.isUsingSQLite();
        
        console.log(`StorageContext: Now using ${isUsingSQLite ? 'SQLite' : 'localStorage'} storage service`);
        
        // If we switched to SQLite, migrate data from localStorage if needed
        if (isUsingSQLite && service !== storageService) {
          console.log('StorageContext: Switched to SQLite, checking for data migration');
          await migrateFromLocalStorage(service);
        }
        
        setStorageService(service);
      } catch (error) {
        console.error('StorageContext: Error re-evaluating storage service', error);
      }
    };

    recheckStorageService();
  }, [authTokenGetter, isInitialized, storageService]);

  /**
   * Migrate data from localStorage to SQLite if needed
   */
  const migrateFromLocalStorage = async (sqliteService) => {
    console.log('StorageContext: Checking if data migration is needed');
    
    try {
      // Check if we have SQLite data already
      const sqliteProjects = await sqliteService.getProjects();
      
      if (sqliteProjects.length === 0) {
        // SQLite is empty, check if we have localStorage data to migrate
        const localProjects = LocalStorageService.getProjects();
        
        if (localProjects.length > 0) {
          console.log(`StorageContext: Migrating ${localProjects.length} projects from localStorage to SQLite`);
          
          // Migrate projects
          for (const project of localProjects) {
            await sqliteService.saveProject(project);
            
            // Migrate vector stores for this project
            const vectorStores = LocalStorageService.getVectorStores(project.id);
            for (const store of vectorStores) {
              await sqliteService.saveVectorStore(store);
            }
            
            // Migrate agents for this project
            const agents = LocalStorageService.getAgents(project.id);
            for (const agent of agents) {
              await sqliteService.saveAgent(agent);
            }
            
            // Migrate layout for this project
            const layout = LocalStorageService.getLayout(project.id);
            if (layout && Object.keys(layout).length > 0) {
              await sqliteService.saveLayout(layout);
            }
          }
          
          // Migrate MCP servers
          const mcpServers = LocalStorageService.getMCPServers();
          for (const server of mcpServers) {
            await sqliteService.saveMCPServer(server);
          }
          
          console.log('StorageContext: Data migration completed successfully');
        } else {
          console.log('StorageContext: No localStorage data to migrate');
        }
      } else {
        console.log(`StorageContext: SQLite database already has ${sqliteProjects.length} projects, skipping migration`);
      }
    } catch (error) {
      console.error('StorageContext: Error during data migration', error);
    }
  };

  // Show loading state while initializing
  if (isLoading) {
    return <div className="p-4 text-center">Loading storage service...</div>;
  }

  return (
    <StorageContext.Provider value={{ 
      storageService, 
      isInitialized, 
      updateAuthTokenGetter 
    }}>
      {children}
    </StorageContext.Provider>
  );
};

/**
 * Hook to use the storage service in components
 */
export const useStorage = () => {
  const context = useContext(StorageContext);
  
  if (!context) {
    throw new Error('useStorage must be used within a StorageProvider');
  }
  
  const { storageService, isInitialized } = context;
  
  if (!isInitialized) {
    console.warn('Storage service is not yet initialized');
  }
  
  return storageService;
};

/**
 * Hook to update auth token getter in storage context
 */
export const useStorageAuth = () => {
  const context = useContext(StorageContext);
  
  if (!context) {
    throw new Error('useStorageAuth must be used within a StorageProvider');
  }
  
  return context.updateAuthTokenGetter;
}; 